import React, { useState, useEffect, useCallback, useRef } from 'react';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import StepHeader from './StepHeader';
import TrainingGroupList from './TrainingGroupList';
import StepNavigation from './StepNavigation';

const Step1TrainingGroupSelection = ({
  trainingGroups,
  selectedGroupId,
  onComplete,
  onBack,
  registerNav,
}) => {
  const [selectedGroup, setSelectedGroup] = useState(selectedGroupId);
  const onCompleteRef = useRef(onComplete);

  // Update ref when onComplete changes
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const handleGroupSelect = (groupId) => {
    setSelectedGroup(groupId);
  };

  const handleContinue = useCallback(() => {
    if (selectedGroup) {
      onCompleteRef.current(selectedGroup);
    }
  }, [selectedGroup]);

  const selectedGroupInfo = React.useMemo(() => {
    if (!selectedGroup) return null;
    return trainingGroups.find((g) => g.id === selectedGroup) || null;
  }, [trainingGroups, selectedGroup]);

  const prevNavRef = useRef(null);
  useEffect(() => {
    if (typeof registerNav !== 'function') return;
    const payload = {
      onNext: handleContinue,
      onBack,
      canNext: !!selectedGroup,
      canBack: true,
    };
    const prev = prevNavRef.current;
    const changed =
      !prev ||
      prev.onNext !== payload.onNext ||
      prev.onBack !== payload.onBack ||
      prev.canNext !== payload.canNext ||
      prev.canBack !== payload.canBack;
    if (changed) {
      registerNav(payload);
      prevNavRef.current = payload;
    }
  }, [registerNav, handleContinue, onBack, selectedGroup]);

  return (
    <div className="space-y-6">
      <StepHeader
        icon={UserGroupIcon}
        title="Select Training Group"
        description="Choose the training group for which you want to create an automated schedule."
      />
      <TrainingGroupList
        trainingGroups={trainingGroups}
        selectedGroup={selectedGroup}
        onGroupSelect={handleGroupSelect}
      />
      {selectedGroupInfo && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Training Group Selected</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  You've selected: <strong>{selectedGroupInfo.name}</strong>
                </p>
                <p className="mt-1">
                  This group will be used for the automated scheduling process.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      <StepNavigation
        onBack={onBack}
        onContinue={handleContinue}
        continueDisabled={!selectedGroup}
      />
    </div>
  );
};

export default Step1TrainingGroupSelection;
